import { useSwitch } from '@mui/base';
import { useThemeProps, styled } from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import getTransition from '../utilities/getTransition';
import { Palette, switchClasses, switchSizes } from './constants';
import { SwitchProps } from './types';
import omit from '../utilities/omit';

const Root = styled('span', {
  name: 'MuiSwitch',
  slot: 'Root',
  overridesResolver: (props, styles) => [styles.root],
})<{ ownerState: SwitchProps }>({
  position: 'relative',
  zIndex: 0,
  display: 'inline-flex',
  verticalAlign: 'middle',
  overflow: 'hidden',
});

const Base = styled('span', {
  name: 'MuiSwitch',
  slot: 'SwitchBase',
  overridesResolver: (props, styles) => [styles.switchBase],
})<{ ownerState: SwitchProps }>({
  position: 'absolute',
  left: 0,
  top: 0,
  zIndex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  margin: 0,
  border: 0,
  borderRadius: '50%',
  outline: 0,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  textDecoration: 'none',
  userSelect: 'none',
  ...getTransition(['left', 'transform']),
  [`&.${switchClasses.disabled}`]: {
    pointerEvents: 'none',
    cursor: 'default',
  },
});

const Track = styled('span', {
  name: 'MuiSwitch',
  slot: 'Track',
  overridesResolver: (props, styles) => [styles.track],
})<{ ownerState: SwitchProps }>({
  width: '100%',
  zIndex: -1,
  outlineWidth: 2,
  outlineOffset: 2,
  outlineStyle: 'solid',
  outlineColor: 'transparent',
  ...getTransition(['opacity', 'background-color', 'outline-color']),
  [`.${switchClasses.focusVisible} &`]: {
    outlineColor: Palette.focus,
  },
});

const Input = styled('input', {
  name: 'MuiSwitch',
  slot: 'Input',
  overridesResolver: (props, styles) => [styles.input],
})<{ ownerState: SwitchProps }>({
  position: 'absolute',
  top: 0,
  left: '-100%',
  zIndex: 1,
  height: '100%',
  width: '300%',
  margin: 0,
  padding: 0,
  opacity: 0,
  cursor: 'inherit',
});

const Thumb = styled('span', {
  name: 'MuiSwitch',
  slot: 'Thumb',
  overridesResolver: (props, styles) => [styles.thumb],
})<{ ownerState: SwitchProps }>({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxSizing: 'border-box',
});

const HoverHalo = styled('span')<{ ownerState: SwitchProps }>(
  ({ ownerState }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: -2,
    display: 'block',
    height: switchSizes[ownerState.size!] + 8,
    width: switchSizes[ownerState.size!] + 8,
    borderRadius: '100%',
    backgroundColor: 'transparent',
    ...getTransition(['left', 'transform', 'background-color']),
    [`.${switchClasses.root}:hover &`]: {
      backgroundColor: Palette.thumb.hover.halo,
    },
    [`.${switchClasses.root}.${switchClasses.disabled}:hover &`]: {
      backgroundColor: 'transparent',
    },
    [`&.${switchClasses.checked}`]: {
      transform: `translateX(${switchSizes[ownerState.size!]}px)`,
    },
  }),
);

// TODO: clean this up https://chemaxon.atlassian.net/browse/UXP-607
export function Switch(inProps: SwitchProps) {
  const props = useThemeProps({ props: inProps, name: 'MuiSwitch' });
  const rootProps = omit([
    'onChange', 'disabled', 'checked', 'id', 'colorful', 'focusRipple', 'disableFocusRipple',
    'disableRipple', 'centerRipple', 'disableTouchRipple', 'disableTouchRipple',
  ], props);

  const {
    getInputProps, checked, disabled, focusVisible,
    /* eslint-disable-next-line */
    // @ts-ignore
  } = useSwitch(props);

  const { onChange, ...inputProps } = getInputProps();

  const stateClasses = {
    [switchClasses.checked]: checked,
    [switchClasses.disabled]: disabled,
    [switchClasses.focusVisible]: focusVisible,
  };

  return (
    <Root
      ownerState={props}
      {...rootProps}
      className={clsx(stateClasses, switchClasses.root, rootProps.className)}
    >
      <Base
        ownerState={props}
        className={clsx(stateClasses, switchClasses.switchBase)}
      >
        <Input
          ownerState={props}
          {...inputProps}
          onChange={(event) => {
            onChange(event);
            props.onChange?.(event, event.target.checked);
          }}
          className={switchClasses.input}
          value={props.value as string | number | undefined}
          id={props.id}
        />
        <Thumb ownerState={props} className={switchClasses.thumb} />
      </Base>
      <Track ownerState={props} className={switchClasses.track} />
      <HoverHalo
        ownerState={props}
        className={clsx(
          {
            [switchClasses.checked]: checked,
          },
          switchClasses.hoverHalo,
        )}
      />
    </Root>
  );
}
